import { useEffect, useRef, useState } from "react";
import mapboxgl, { Map as MapboxMap, Marker } from "mapbox-gl";
import { supported } from "@mapbox/mapbox-gl-supported";

import { Coordinate } from "common/types";
import { useMapboxCSS } from "common/hooks/mapbox";
import { getMapBounds } from "common/utils/geo";
import horseWithLetter from "common/images/horse-with-letter.svg";

import Image from "./Image";

mapboxgl.accessToken = window.MAPBOX_BROWSER_KEY;

export type CommonMapProps = {
  className?: string;
  style?: React.CSSProperties;
  /**
   * A list of coordinate pairs to render markers for
   * Consumers should memoize this prop if performance ever becomes an issue
   * */
  markers?: Coordinate[];
  /**
   * A list of coordinate pairs to draw paths between
   * Consumers should memoize this prop if performance ever becomes an issue
   * */
  paths?: Coordinate[];
};

/**
 * Supported options for customizing the style of the map paths
 * */
type PathOptions = {
  paint: {
    "line-color": string;
    "line-width": number;
  };
};

type MapProps = CommonMapProps & {
  /** Which mapbox style to use */
  mapStyle: string;
  /**
   * Callback for creating a map marker, run on each set of coordinates in markers
   * Should be memoized
   * */
  createMarker: (marker: Coordinate) => Marker;
  /**
   * Options for customizing the appearance of the map paths
   * */
  pathOptions: PathOptions;
  /** What to render while mapbox is loading */
  renderLoading: () => React.ReactNode;
  /** Whether the map can be scrolled to zoom */
  disableZoom?: boolean;
};

/** Renders a Map using Mapbox-GL JS
 * When using this componenet, lazy-load it to reduce bundle size (mapbox-gl is 30+mb)
 */
const Map = ({
  markers = [],
  paths = [],
  mapStyle,
  createMarker,
  pathOptions,
  renderLoading,
  disableZoom = false,
  ...other
}: MapProps) => {
  const isMapboxLoaded = useMapboxCSS();
  const [isMapboxSupported, setIsMapboxSupported] = useState(supported());
  const mapContainer = useRef<HTMLDivElement>(null);

  // Create the Map
  useEffect(() => {
    if (!mapContainer.current || !isMapboxLoaded || !isMapboxSupported) {
      return;
    }

    try {
      const map = new MapboxMap({
        container: mapContainer.current,
        style: mapStyle,
        bounds: getMapBounds([...markers, ...paths]),
      });

      if (disableZoom) {
        map.scrollZoom.disable();
      }

      // Add markers + path lines for the set of provided coordinates
      if (markers.length) {
        markers.forEach((coordinate) => {
          const marker = createMarker(coordinate);
          marker.addTo(map);
        });
      }

      if (paths.length) {
        map.on("load", () => {
          // Add lines between each pair of coordinates
          map.addSource("route", {
            type: "geojson",
            data: {
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: paths,
              },
            },
          });
          map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            ...pathOptions,
          });
        });
      }
    } catch {
      // In case mapbox-gl-supported doesn't work (for very obscure browsers)
      setIsMapboxSupported(false);
    }
  }, [
    isMapboxLoaded,
    isMapboxSupported,
    markers,
    paths,
    mapStyle,
    disableZoom,
    createMarker,
    pathOptions,
  ]);

  if (!isMapboxSupported) {
    return (
      <div {...other}>
        <Image src={horseWithLetter} alt="horse with letter" />
      </div>
    );
  }

  if (!isMapboxLoaded) {
    return <div {...other}>{renderLoading()}</div>;
  }

  return <div {...other} ref={mapContainer} />;
};

export default Map;
